import { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Hide react-big-calendar's default toolbar on mobile
const hideToolbarStyles = `
  @media (max-width: 600px) {
    .rbc-toolbar {
      display: none !important;
    }
    .rbc-header {
      font-size: 0.75rem;
      padding: 8px 4px;
    }
    .rbc-date-cell {
      font-size: 0.75rem;
      padding: 4px 2px;
    }
    .rbc-event {
      font-size: 0.7rem;
      padding: 2px 4px;
      min-height: 18px;
    }
  }
`;
import { Box, Paper, Typography, Button, Chip, IconButton, Tooltip, Popover, Divider, TextField, DialogContentText, Card, CardContent, CardActions, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import apiClient from '../../services/apiClient';
import socketClient from '../../services/socketClient';
import { cancelAppointment, rescheduleAppointment, deleteAppointment } from '../../services/appointmentsService';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import toast from 'react-hot-toast';

// Icons
import TodayIcon from '@mui/icons-material/Today';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';

const localizer = momentLocalizer(moment);

const CalendarView = ({ 
  onSelectSlot, 
  height = 600,
  showToolbar = true,
  defaultView = Views.MONTH 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { getEffectiveTenantId } = useView();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(defaultView);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [userData, setUserData] = useState({}); // Cache for patient/physician data
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState(null);

  // Fetch user data (patients/physicians) for appointments
  const fetchUserDataForAppointments = useCallback(async (appointmentsData) => {
    if (!appointmentsData || appointmentsData.length === 0) return;

    try {
      // Collect unique user IDs from both patient and provider fields
      const patientIds = [...new Set(appointmentsData.map(a => a.patient_id).filter(Boolean))];
      const providerIds = [...new Set(appointmentsData.map(a => a.provider_id || a.physician_id).filter(Boolean))];
      const allUserIds = [...new Set([...patientIds, ...providerIds])];

      if (allUserIds.length === 0) return;

      // Fetch each user directly by ID (no tenant dependency)
      const results = await Promise.allSettled(
        allUserIds.map(id => apiClient.getUserById(id))
      );

      const userMap = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const u = result.value;
          userMap[u.id] = {
            id: u.id,
            name: `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || 'Unknown',
            email: u.email,
            first_name: u.first_name || u.firstName,
            last_name: u.last_name || u.lastName
          };
        }
      });

      setUserData(userMap);
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Error fetching user data for appointments:', error);
    }
  }, []);

  // Get appointments from API
  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const tenantId = getEffectiveTenantId();
      
      // Get appointments for the current month (with buffer)
      const startDate = moment(currentDate).startOf('month').subtract(1, 'month').toISOString();
      const endDate = moment(currentDate).endOf('month').add(1, 'month').toISOString();
      
      const response = await apiClient.getAppointments({
        tenant_id: tenantId,
        start_date: startDate,
        end_date: endDate,
        limit: 100 // API maximum limit
      });

      // Handle different response formats
      let appointmentsData = [];
      if (Array.isArray(response)) {
        appointmentsData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        appointmentsData = response.data;
      } else if (response?.rows && Array.isArray(response.rows)) {
        appointmentsData = response.rows;
      }

      setAppointments(appointmentsData);

      // Fetch user data for all appointments
      await fetchUserDataForAppointments(appointmentsData);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [currentDate, getEffectiveTenantId, fetchUserDataForAppointments]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Listen for real-time appointment updates
  useEffect(() => {
    const handleAppointmentUpdate = () => {
      fetchAppointments();
    };

    socketClient.on('appointment:created', handleAppointmentUpdate);
    socketClient.on('appointment:updated', handleAppointmentUpdate);
    socketClient.on('appointment:deleted', handleAppointmentUpdate);

    return () => {
      socketClient.off('appointment:created', handleAppointmentUpdate);
      socketClient.off('appointment:updated', handleAppointmentUpdate);
      socketClient.off('appointment:deleted', handleAppointmentUpdate);
    };
  }, [fetchAppointments]);

  // Transform appointments to calendar events
  const events = useMemo(() => {
    return appointments
      .filter(appt => {
        // Filter out appointments with invalid dates
        const scheduledTime = appt.scheduled_time || appt.scheduled_at || appt.appointment_date;
        if (!scheduledTime) return false;
        const date = new Date(scheduledTime);
        return !isNaN(date.getTime()) && date.getTime() > 0;
      })
      .map(appt => {
        const scheduledTime = appt.scheduled_time || appt.scheduled_at || appt.appointment_date;
        const start = new Date(scheduledTime);
        const duration = appt.duration_minutes || appt.duration || 30;
        const end = new Date(start.getTime() + duration * 60000);

        // Determine event color based on status
        const getEventStyle = () => {
          const status = appt.status?.toLowerCase();
          switch (status) {
            case 'pending':
              return { backgroundColor: '#ff9800', color: '#fff' };
            case 'confirmed':
            case 'scheduled':
              return { backgroundColor: '#4caf50', color: '#fff' };
            case 'in_progress':
              return { backgroundColor: '#2196f3', color: '#fff' };
            case 'completed':
              return { backgroundColor: '#9e9e9e', color: '#fff' };
            case 'cancelled':
            case 'rejected':
              return { backgroundColor: '#f44336', color: '#fff' };
            default:
              return { backgroundColor: '#757575', color: '#fff' };
          }
        };

        // Get title based on user role - use fetched user data
        let title = 'Appointment';
        if (user?.tenant_role === 'patient') {
          // For patients, show physician name
          const providerId = appt.provider_id || appt.physician_id;
          const provider = userData[providerId];
          title = provider?.name || appt.physician?.name || appt.provider?.name || 
                  appt.physician?.first_name || appt.provider?.first_name || 
                  'Physician';
        } else {
          // For physicians/admins, show patient name
          const patient = userData[appt.patient_id];
          title = patient?.name || appt.patient?.name || 
                  appt.patient?.first_name || 
                  'Patient';
        }

        return {
          id: appt.id,
          title,
          start,
          end,
          resource: appt,
          style: getEventStyle()
        };
      });
  }, [appointments, user, userData]);

  // Handle date navigation
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setCurrentView(newView);
  };

  // Handle slot selection (clicking empty time slot)
  const handleSelectSlot = useCallback(({ start, end }) => {
    if (onSelectSlot) {
      onSelectSlot({ start, end });
    }
  }, [onSelectSlot]);

  // Handle event selection (clicking appointment)
  const handleSelectEvent = useCallback((event, e) => {
    // Prevent any default behavior or navigation
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      // For react-big-calendar, we need to prevent the default event handling
      if (e.nativeEvent) {
        if (typeof e.nativeEvent.preventDefault === 'function') e.nativeEvent.preventDefault();
        if (typeof e.nativeEvent.stopPropagation === 'function') e.nativeEvent.stopPropagation();
        if (typeof e.nativeEvent.stopImmediatePropagation === 'function') e.nativeEvent.stopImmediatePropagation();
      }
      // Also check for originalEvent (some libraries use this)
      if (e.originalEvent) {
        if (typeof e.originalEvent.preventDefault === 'function') e.originalEvent.preventDefault();
        if (typeof e.originalEvent.stopPropagation === 'function') e.originalEvent.stopPropagation();
      }
    }
    
    setSelectedEvent(event.resource);
    
    // Try to get a proper anchor element from the event
    let anchor = null;
    let position = { top: 0, left: 0 };
    
    if (e) {
      // Try different ways to get the anchor element
      anchor = e.currentTarget || e.target;
      
      // If we have a target, try to get its position
      if (e.target && e.target.getBoundingClientRect) {
        const rect = e.target.getBoundingClientRect();
        position = {
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width / 2
        };
      } else if (e.clientX && e.clientY) {
        // Use mouse position as fallback
        position = {
          top: e.clientY,
          left: e.clientX
        };
      }
    }
    
    // If no anchor found, use body and center position
    if (!anchor) {
      anchor = document.body;
      position = {
        top: window.innerHeight / 2,
        left: window.innerWidth / 2
      };
    }
    
    setAnchorEl(anchor);
    setPopoverPosition(position);
    setPopoverOpen(true);
    
    // Don't call parent's onSelectEvent - we're handling it with the popover
    // Parent components should use the popover actions instead
    
    // Return false to prevent any default calendar behavior
    return false;
  }, []);

  const handleClosePopover = () => {
    setPopoverOpen(false);
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const refreshAppointments = async () => {
    await fetchAppointments();
  };

  const handleCancelClick = () => {
    if (selectedEvent) {
      setCancelReason('');
      setCancelDialogOpen(true);
      handleClosePopover();
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedEvent) return;
    
    const { error } = await cancelAppointment(selectedEvent.id, cancelReason);
    if (!error) {
      setCancelDialogOpen(false);
      setSelectedEvent(null);
      setCancelReason('');
      await refreshAppointments();
      toast.success('Appointment cancelled successfully');
    }
  };

  const handleRescheduleClick = () => {
    if (selectedEvent) {
      if (selectedEvent.scheduled_time) {
        const date = new Date(selectedEvent.scheduled_time);
        setRescheduleDate(date);
        setRescheduleTime(date);
      } else {
        setRescheduleDate(new Date());
        setRescheduleTime(new Date());
      }
      setRescheduleDialogOpen(true);
      handleClosePopover();
    }
  };

  const handleRescheduleConfirm = async () => {
    if (!selectedEvent || !rescheduleDate) return;
    
    // Combine date and time
    let scheduledDateTime = new Date(rescheduleDate);
    if (rescheduleTime) {
      scheduledDateTime.setHours(rescheduleTime.getHours());
      scheduledDateTime.setMinutes(rescheduleTime.getMinutes());
      scheduledDateTime.setSeconds(0);
      scheduledDateTime.setMilliseconds(0);
    } else {
      scheduledDateTime.setHours(9, 0, 0, 0);
    }

    const { error } = await rescheduleAppointment(selectedEvent.id, scheduledDateTime.toISOString());
    if (!error) {
      setRescheduleDialogOpen(false);
      setSelectedEvent(null);
      setRescheduleDate(null);
      setRescheduleTime(null);
      await refreshAppointments();
      toast.success('Appointment rescheduled successfully');
    }
  };

  const handleDeleteClick = async () => {
    if (!selectedEvent) return;
    if (window.confirm('Are you sure you want to delete this appointment? This action cannot be undone.')) {
      const { error } = await deleteAppointment(selectedEvent.id);
      if (!error) {
        handleClosePopover();
        await refreshAppointments();
        toast.success('Appointment deleted successfully');
      }
    }
  };

  const handleStartVideoCall = () => {
    if (selectedEvent) {
      handleClosePopover();
      navigate(`/video-call?roomId=${selectedEvent.video_call_room_id || selectedEvent.id}`);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'pending': return 'warning';
      case 'confirmed':
      case 'scheduled': return 'success';
      case 'in_progress': return 'info';
      case 'completed': return 'default';
      case 'cancelled':
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'TBD';
    return moment(dateString).format('MMM DD, YYYY [at] h:mm A');
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <style>{hideToolbarStyles}</style>
      {showToolbar && (
        <Box sx={{ 
          mb: { xs: 1.5, sm: 2.5 },
          px: { xs: 0.5, sm: 0 }
        }}>
          {/* Mobile: Single compact row */}
          {isMobile ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1
            }}>
              {/* View switcher - compact buttons */}
              <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                <Button
                  variant={currentView === Views.MONTH ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleViewChange(Views.MONTH)}
                  sx={{ 
                    minWidth: 0,
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.75rem',
                    flex: 1
                  }}
                >
                  Month
                </Button>
                <Button
                  variant={currentView === Views.WEEK ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleViewChange(Views.WEEK)}
                  sx={{ 
                    minWidth: 0,
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.75rem',
                    flex: 1
                  }}
                >
                  Week
                </Button>
                <Button
                  variant={currentView === Views.DAY ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleViewChange(Views.DAY)}
                  sx={{ 
                    minWidth: 0,
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.75rem',
                    flex: 1
                  }}
                >
                  Day
                </Button>
              </Box>
              
              {/* Navigation - compact */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton 
                  size="small"
                  onClick={() => handleNavigate(moment(currentDate).subtract(1, currentView === Views.MONTH ? 'month' : currentView === Views.WEEK ? 'week' : 'day').toDate())}
                  sx={{ p: 0.75 }}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleNavigate(new Date())}
                  sx={{ 
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    minWidth: 0
                  }}
                >
                  Today
                </Button>
                <IconButton 
                  size="small"
                  onClick={() => handleNavigate(moment(currentDate).add(1, currentView === Views.MONTH ? 'month' : currentView === Views.WEEK ? 'week' : 'day').toDate())}
                  sx={{ p: 0.75 }}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            /* Desktop: Two-row layout */
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center'
              }}>
                <Button
                  variant={currentView === Views.MONTH ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<TodayIcon />}
                  onClick={() => handleViewChange(Views.MONTH)}
                >
                  Month
                </Button>
                <Button
                  variant={currentView === Views.WEEK ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<ViewWeekIcon />}
                  onClick={() => handleViewChange(Views.WEEK)}
                >
                  Week
                </Button>
                <Button
                  variant={currentView === Views.DAY ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<ViewDayIcon />}
                  onClick={() => handleViewChange(Views.DAY)}
                >
                  Day
                </Button>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center'
              }}>
                <IconButton 
                  size="small"
                  onClick={() => handleNavigate(moment(currentDate).subtract(1, currentView === Views.MONTH ? 'month' : currentView === Views.WEEK ? 'week' : 'day').toDate())}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleNavigate(new Date())}
                >
                  Today
                </Button>
                <IconButton 
                  size="small"
                  onClick={() => handleNavigate(moment(currentDate).add(1, currentView === Views.MONTH ? 'month' : currentView === Views.WEEK ? 'week' : 'day').toDate())}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            </Box>
          )}
          
          {/* Current date display - mobile only */}
          {isMobile && (
            <Typography 
              variant="subtitle2" 
              sx={{ 
                textAlign: 'center',
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.875rem',
                mt: 0.5
              }}
            >
              {moment(currentDate).format('MMMM YYYY')}
            </Typography>
          )}
        </Box>
      )}

      <Paper 
        elevation={0}
        sx={{ 
          p: showToolbar ? { xs: 1, sm: 2, md: 2.5 } : 0, 
          borderRadius: showToolbar ? 2 : 0,
          bgcolor: 'background.paper',
          height: height,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Loading appointments...
            </Typography>
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ 
              height: showToolbar 
                ? (isMobile ? height - 140 : isTablet ? height - 110 : height - 100)
                : height - 32 
            }}
            view={currentView}
            onView={handleViewChange}
            date={currentDate}
            onNavigate={handleNavigate}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onDoubleClickEvent={(event, e) => {
              // Prevent double-click from doing anything - just show popover
              if (e) {
                if (typeof e.preventDefault === 'function') e.preventDefault();
                if (typeof e.stopPropagation === 'function') e.stopPropagation();
              }
              // Treat double-click same as single click
              handleSelectEvent(event, e);
            }}
            selectable
            popup={false}
            formats={{
              dayFormat: (date, culture, localizer) => localizer.format(date, 'ddd', culture),
              dayHeaderFormat: (date, culture, localizer) => localizer.format(date, 'ddd', culture),
              dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, 'MMM DD', culture) + ' - ' + localizer.format(end, 'MMM DD', culture),
              monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'MMMM YYYY', culture),
            }}
            components={{
              event: ({ event }) => {
                // Custom event component - just render the event, let react-big-calendar handle clicks
                return (
                  <div
                    style={{
                      ...event.style,
                      width: '100%',
                      height: '100%',
                      padding: isMobile ? '1px 2px' : '2px 4px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      pointerEvents: 'auto',
                      fontSize: isMobile ? '0.7rem' : '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {event.title}
                  </div>
                );
              }
            }}
            eventPropGetter={(event) => ({
              style: event.style || { backgroundColor: '#757575', color: '#fff' }
            })}
            messages={{
              next: 'Next',
              previous: 'Previous',
              today: 'Today',
              month: 'Month',
              week: 'Week',
              day: 'Day',
              agenda: 'Agenda',
              date: 'Date',
              time: 'Time',
              event: 'Event',
              noEventsInRange: 'No appointments in this range'
            }}
          />
        )}
      </Paper>

      {/* Appointment Mini Card Popover */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl !== document.body ? anchorEl : null}
        anchorReference={anchorEl === document.body ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={anchorEl === document.body ? popoverPosition : undefined}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: anchorEl !== document.body ? 1 : 0,
            minWidth: { xs: '280px', sm: 320 },
            maxWidth: { xs: '90vw', sm: 400 },
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            borderRadius: 2,
          }
        }}
        disableRestoreFocus
        onClick={(e) => {
          // Prevent any clicks inside popover from bubbling to calendar
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Prevent any mouse events from bubbling
          e.stopPropagation();
        }}
      >
        {selectedEvent && (
          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    {user?.tenant_role === 'patient'
                      ? (userData[selectedEvent.provider_id || selectedEvent.physician_id]?.name ||
                         selectedEvent.physician?.name || 
                         selectedEvent.provider?.name || 
                         selectedEvent.physician?.first_name || 
                         selectedEvent.provider?.first_name || 
                         'Unknown Physician')
                      : (userData[selectedEvent.patient_id]?.name ||
                         selectedEvent.patient?.name || 
                         selectedEvent.patient?.first_name || 
                         'Unknown Patient')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <AccessTimeIcon fontSize="small" />
                    {formatDateTime(selectedEvent.scheduled_time || selectedEvent.scheduled_at || selectedEvent.appointment_date)}
                  </Typography>
                  <Chip 
                    label={selectedEvent.status || 'Unknown'} 
                    color={getStatusColor(selectedEvent.status)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <IconButton 
                  size="small" 
                  onClick={handleClosePopover}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {selectedEvent.notes && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Notes:</strong> {selectedEvent.notes}
                  </Typography>
                </>
              )}
            </CardContent>
            <Divider />
            <CardActions sx={{ p: { xs: 1, sm: 1.5 }, pt: 1, flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
              {selectedEvent && (selectedEvent.status === 'scheduled' || selectedEvent.status === 'confirmed') && (
                <>
                  <Button
                    variant="contained"
                    size={isMobile ? 'small' : 'small'}
                    startIcon={!isMobile ? <VideocamIcon /> : null}
                    onClick={handleStartVideoCall}
                    sx={{ 
                      flex: { xs: '1 1 100%', sm: 1 }, 
                      minWidth: { xs: '100%', sm: '120px' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {isMobile ? 'Call' : 'Start Call'}
                  </Button>
                  {(user?.tenant_role === 'physician' || user?.platform_role === 'super_admin' || user?.tenant_role === 'clinic_staff') && (
                    <>
                      <Button
                        variant="outlined"
                        size={isMobile ? 'small' : 'small'}
                        startIcon={!isMobile ? <EditIcon /> : null}
                        onClick={handleRescheduleClick}
                        sx={{ 
                          flex: { xs: '1 1 calc(50% - 4px)', sm: 1 }, 
                          minWidth: { xs: 'auto', sm: '100px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        {isMobile ? 'Resched' : 'Reschedule'}
                      </Button>
                      <Button
                        variant="outlined"
                        size={isMobile ? 'small' : 'small'}
                        color="error"
                        startIcon={!isMobile ? <CancelIcon /> : null}
                        onClick={handleCancelClick}
                        sx={{ 
                          flex: { xs: '1 1 calc(50% - 4px)', sm: 1 }, 
                          minWidth: { xs: 'auto', sm: '100px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </>
              )}
              {selectedEvent && (selectedEvent.status === 'cancelled' || selectedEvent.status === 'completed') && (
                (user?.tenant_role === 'physician' || user?.platform_role === 'super_admin' || user?.tenant_role === 'clinic_staff') && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                    fullWidth
                  >
                    Delete
                  </Button>
                )
              )}
            </CardActions>
          </Card>
        )}
      </Popover>

      {/* Cancel Appointment Dialog */}
      <Popover
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 0,
            minWidth: 400,
            maxWidth: 500,
          }
        }}
      >
        <Card elevation={0}>
          <CardContent sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Cancel Appointment</Typography>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to cancel this appointment? This action can be undone by rescheduling.
            </DialogContentText>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for cancellation (optional)"
              placeholder="e.g., Patient requested, scheduling conflict, etc."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </CardContent>
          <Divider />
          <CardActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setCancelDialogOpen(false)}>Keep Appointment</Button>
            <Button onClick={handleCancelConfirm} color="error" variant="contained">
              Cancel Appointment
            </Button>
          </CardActions>
        </Card>
      </Popover>

      {/* Reschedule Appointment Dialog */}
      <Popover
        open={rescheduleDialogOpen}
        onClose={() => setRescheduleDialogOpen(false)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 0,
            minWidth: 400,
            maxWidth: 500,
          }
        }}
      >
        <Card elevation={0}>
          <CardContent sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Reschedule Appointment</Typography>
            <DialogContentText sx={{ mb: 2 }}>
              Select a new date and time for this appointment.
            </DialogContentText>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="New Date"
                value={rescheduleDate}
                onChange={setRescheduleDate}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { mb: 2 }
                  }
                }}
              />
              <TimePicker
                label="New Time"
                value={rescheduleTime}
                onChange={setRescheduleTime}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </CardContent>
          <Divider />
          <CardActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setRescheduleDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleRescheduleConfirm} 
              variant="contained"
              disabled={!rescheduleDate}
            >
              Reschedule
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </Box>
  );
};

export default CalendarView;

